import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, notFoundIdHandler, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  EstadoContrato,
  EstadoContratoPagingRes,
  MutationParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchEstadoContratos = (params?: GetEstadoContratosParams) => {
  return useQuery({
    queryKey: ['estado-contratos', ...Object.values(params || {})],
    queryFn: () => getEstadoContratos(params),
  });
};

export const useGetEstadoContrato = (id: string) => {
  return useQuery({
    queryKey: ['estado-contrato', id],
    queryFn: () => getEstadoContrato(id),
  });
};

export const useCreateEstadoContrato = ({
  navigate,
  returnUrl,
}: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEstadoContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-contratos'] });
      navigate && returnUrl && navigate(returnUrl);
    },
  });
};

export const useUpdateEstadoContrato = ({
  navigate,
  returnUrl,
}: MutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEstadoContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-contratos'] });
      navigate && returnUrl && navigate(returnUrl);
    },
  });
};

export const useDeleteEstadoContrato = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEstadoContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estado-contratos'] });
    },
    onError: () => {},
  });
};

///* axios
export type GetEstadoContratosParams = {
  page?: number;
  page_size?: number;

  // filters
  nombre?: string;
  codigo?: string;
};
export type CreateEstadoContratoParams = Omit<
  EstadoContrato,
  'id_estado_contrato'
>;
export type UpdateEstadoContratoParams = {
  id: string;
  data: CreateEstadoContratoParams;
};

export const getEstadoContratos = async (params?: GetEstadoContratosParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<EstadoContratoPagingRes>(
    `${urlBackend}api/v1/estados-contrato/?${queryParams}`,
    true
  );
};

export const getEstadoContrato = async (id: string) => {
  const res = await get<EstadoContrato>(
    `${urlBackend}api/v1/estados-contrato/${id}`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createEstadoContrato = async (
  data: CreateEstadoContratoParams
) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await post<EstadoContrato>(
    `${urlBackend}api/v1/estados-contrato/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateEstadoContrato = async ({
  id,
  data,
}: UpdateEstadoContratoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<EstadoContrato>(
    `${urlBackend}api/v1/estados-contrato/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const deleteEstadoContrato = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<EstadoContrato>(
    `${urlBackend}api/v1/estados-contrato/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
